import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer 
} from 'recharts';
import API_URL from './config';

function PerformanceReports() {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('asc-performance');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const COLORS = ['#007AFF', '#34C759', '#FF9500', '#FF3B30', '#AF52DE', '#5AC8FA', '#5856D6'];

  useEffect(() => {
    fetchReportData();
  }, [reportType, dateRange]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      console.log(`Fetching: ${API_URL}/api/reports/performance/${reportType}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
      
      const response = await axios.get(`${API_URL}/api/reports/performance/${reportType}`, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        },
        headers: { Authorization: `Bearer ${token}` }
      });
  
      console.log('Response data:', response.data);
      setReportData(response.data);
      setMessage({ type: 'success', text: 'Report loaded successfully' });
    } catch (error) {
      console.error('Fetch error:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.status === 404) {
        setMessage({ type: 'error', text: `Report type '${reportType}' not found. Please try another.` });
      } else {
        setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to load report data' });
      }
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (format = 'excel') => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/reports/performance/${reportType}/download`, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          format
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-${dateRange.startDate}-to-${dateRange.endDate}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showMessage('error', 'Download failed');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const renderASCPerformance = () => {
    if (!reportData?.ascData) {
      return (
        <div style={styles.noData}>
          <h3 style={styles.noDataTitle}>No ASC performance data available</h3>
          <p style={styles.noDataText}>Try adjusting the date range or check if there are any allocated jobs.</p>
        </div>
      );
    }
  
    if (reportData.ascData.length === 0) {
      return (
        <div style={styles.noData}>
          <div style={styles.noDataIcon}>🔍</div>
          <h3 style={styles.noDataTitle}>No data found for selected date range</h3>
          <p style={styles.noDataText}>Date range: {dateRange.startDate} to {dateRange.endDate}</p>
          <p style={styles.noDataText}>Try a different date range or check if jobs were allocated during this period.</p>
        </div>
      );
    }
  
    return (
      <div style={styles.fadeIn}>
        {/* Top Performing ASCs */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>🏆 Top Performing ASCs</h3>
            <div style={styles.dateRangeBadge}>
              {dateRange.startDate} → {dateRange.endDate}
            </div>
          </div>
          
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>ASC Name</th>
                  <th>Total Jobs</th>
                  <th>Completed</th>
                  <th>Cancelled</th>
                  <th>Avg TAT</th>
                  <th>Email Success</th>
                  <th>Performance</th>
                </tr>
              </thead>
              <tbody>
                {reportData.ascData.map((asc, index) => {
                  const completionRate = asc.completion_rate || 0;
                  return (
                    <tr key={asc.asc_name} style={styles.tableRow}>
                      <td>
                        <div style={{
                          ...styles.rankBadge,
                          backgroundColor: index === 0 ? '#FFD70020' : index === 1 ? '#C0C0C020' : index === 2 ? '#CD7F3220' : 'transparent',
                          color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'var(--system-label)'
                        }}>
                          #{index + 1}
                        </div>
                      </td>
                      <td><strong>{asc.asc_name}</strong></td>
                      <td>{formatNumber(asc.total_jobs)}</td>
                      <td style={{ color: 'var(--system-green)' }}>{formatNumber(asc.completed)}</td>
                      <td style={{ color: 'var(--system-red)' }}>{formatNumber(asc.cancelled)}</td>
                      <td>
                        <div style={styles.tatCell}>
                          <span>{asc.avg_tat?.toFixed(1) || '0.0'}d</span>
                          <span style={{
                            ...styles.tatIndicator,
                            backgroundColor: (asc.avg_tat || 0) <= 2 ? 'var(--system-green)' : 
                                          (asc.avg_tat || 0) <= 4 ? 'var(--system-orange)' : 'var(--system-red)'
                          }} />
                        </div>
                      </td>
                      <td>
                        <div style={styles.emailSuccessCell}>
                          <span>{asc.email_success_rate || 0}%</span>
                          <div style={styles.miniProgress}>
                            <div style={{
                              ...styles.miniProgressFill,
                              width: `${asc.email_success_rate || 0}%`,
                              backgroundColor: (asc.email_success_rate || 0) > 70 ? 'var(--system-green)' : 'var(--system-orange)'
                            }} />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={styles.progressBar}>
                          <div style={{
                            ...styles.progressFill,
                            width: `${completionRate}%`,
                            backgroundColor: completionRate > 70 ? 'var(--system-green)' : 
                                          completionRate > 40 ? 'var(--system-orange)' : 'var(--system-red)'
                          }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
  
        {/* ASC Performance Chart */}
        {reportData.ascData.length > 0 && (
          <div style={styles.chartCard}>
            <h3 style={styles.chartTitle}>ASC Job Distribution</h3>
            <div style={styles.chartWrapper}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={reportData.ascData.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--system-separator)" />
                  <XAxis 
                    dataKey="asc_name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100} 
                    tick={{ fill: 'var(--system-secondary-label)', fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: 'var(--system-secondary-label)' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--glass-background)', 
                      backdropFilter: 'blur(10px)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: '12px',
                      boxShadow: 'var(--system-shadow-lg)'
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="completed" fill="#34C759" name="Completed" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cancelled" fill="#FF3B30" name="Cancelled" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pending" fill="#FF9500" name="Pending" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTATReport = () => {
    if (!reportData?.tatData) {
      return (
        <div style={styles.noData}>
          <div style={styles.noDataIcon}>⏱️</div>
          <h3 style={styles.noDataTitle}>No TAT data available</h3>
        </div>
      );
    }

    return (
      <div style={styles.fadeIn}>
        {/* TAT Summary Cards */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statContent}>
              <span style={styles.statLabel}>Average TAT</span>
              <span style={styles.statValue}>{reportData.tatData.avg_tat.toFixed(1)} days</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>⚡</div>
            <div style={styles.statContent}>
              <span style={styles.statLabel}>Fastest</span>
              <span style={styles.statValue}>{reportData.tatData.min_tat.toFixed(1)} days</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>🐢</div>
            <div style={styles.statContent}>
              <span style={styles.statLabel}>Slowest</span>
              <span style={styles.statValue}>{reportData.tatData.max_tat.toFixed(1)} days</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>✅</div>
            <div style={styles.statContent}>
              <span style={styles.statLabel}>Within SLA</span>
              <span style={styles.statValue}>{reportData.tatData.within_sla}%</span>
            </div>
          </div>
        </div>

        {/* TAT Distribution Chart */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>TAT Distribution</h3>
          <div style={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={reportData.tatData.distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--system-separator)" />
                <XAxis dataKey="range" tick={{ fill: 'var(--system-secondary-label)' }} />
                <YAxis tick={{ fill: 'var(--system-secondary-label)' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--glass-background)', 
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: '12px'
                  }} 
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#007AFF" 
                  strokeWidth={3}
                  dot={{ fill: '#007AFF', strokeWidth: 2 }}
                  activeDot={{ r: 8 }}
                  name="Jobs"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Slowest Jobs */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>⚠️ Jobs Exceeding SLA</h3>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th>Job No.</th>
                  <th>ASC</th>
                  <th>Customer</th>
                  <th>Allocation Date</th>
                  <th>Close Date</th>
                  <th>TAT</th>
                </tr>
              </thead>
              <tbody>
                {reportData.tatData.slow_jobs?.map(job => (
                  <tr key={job.job_no} style={styles.tableRow}>
                    <td><strong style={{ color: 'var(--system-blue)' }}>{job.job_no}</strong></td>
                    <td>{job.asc_name}</td>
                    <td>{job.customer_name}</td>
                    <td>{new Date(job.allocation_date).toLocaleDateString()}</td>
                    <td>{job.close_date ? new Date(job.close_date).toLocaleDateString() : 'Pending'}</td>
                    <td>
                      <span style={{
                        ...styles.tatBadge,
                        backgroundColor: job.tat > 5 ? 'var(--system-red)' : 
                                      job.tat > 2 ? 'var(--system-orange)' : 'var(--system-green)'
                      }}>
                        {job.tat.toFixed(1)}d
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderBrandPerformance = () => {
    if (!reportData?.brandData) {
      return (
        <div style={styles.noData}>
          <div style={styles.noDataIcon}>🏷️</div>
          <h3 style={styles.noDataTitle}>No brand data available</h3>
        </div>
      );
    }

    return (
      <div style={styles.fadeIn}>
        <div style={styles.chartRow}>
          {/* Brand Distribution Pie */}
          <div style={styles.chartCard}>
            <h3 style={styles.chartTitle}>Jobs by Brand</h3>
            <div style={styles.chartWrapper}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={reportData.brandData.distribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {reportData.brandData.distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--glass-background)', 
                      backdropFilter: 'blur(10px)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: '12px'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Brand Performance Table */}
          <div style={styles.chartCard}>
            <h3 style={styles.chartTitle}>Brand Performance</h3>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Total</th>
                    <th>Completed</th>
                    <th>Avg TAT</th>
                    <th>Success</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.brandData.performance.map(brand => (
                    <tr key={brand.name} style={styles.tableRow}>
                      <td><strong>{brand.name}</strong></td>
                      <td>{formatNumber(brand.total)}</td>
                      <td style={{ color: 'var(--system-green)' }}>{formatNumber(brand.completed)}</td>
                      <td>{brand.avg_tat.toFixed(1)}d</td>
                      <td>
                        <span style={{
                          ...styles.successBadge,
                          backgroundColor: brand.success_rate > 70 ? 'var(--system-green)' : 
                                        brand.success_rate > 40 ? 'var(--system-orange)' : 'var(--system-red)'
                        }}>
                          {brand.success_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderZonePerformance = () => {
    if (!reportData?.zoneData) {
      return (
        <div style={styles.noData}>
          <div style={styles.noDataIcon}>🗺️</div>
          <h3 style={styles.noDataTitle}>No zone data available</h3>
        </div>
      );
    }

    return (
      <div style={styles.fadeIn}>
        <div style={styles.zoneGrid}>
          {reportData.zoneData.map(zone => (
            <div key={zone.name} style={styles.zoneCard}>
              <div style={styles.zoneHeader}>
                <h4>{zone.name}</h4>
                <span style={styles.zoneBadge}>{zone.asm_count} ASMs</span>
              </div>
              
              <div style={styles.zoneStats}>
                <div style={styles.zoneStat}>
                  <span style={styles.zoneStatLabel}>Total Jobs</span>
                  <span style={styles.zoneStatValue}>{formatNumber(zone.total)}</span>
                </div>
                <div style={styles.zoneStat}>
                  <span style={styles.zoneStatLabel}>Completed</span>
                  <span style={{...styles.zoneStatValue, color: 'var(--system-green)'}}>
                    {formatNumber(zone.completed)}
                  </span>
                </div>
                <div style={styles.zoneStat}>
                  <span style={styles.zoneStatLabel}>Avg TAT</span>
                  <span style={styles.zoneStatValue}>{zone.avg_tat.toFixed(1)}d</span>
                </div>
              </div>

              <div style={styles.zoneProgress}>
                <div style={styles.progressBar}>
                  <div style={{
                    ...styles.progressFill,
                    width: `${(zone.completed / zone.total) * 100}%`,
                    backgroundColor: 'var(--system-green)'
                  }} />
                </div>
                <span style={styles.zoneProgressLabel}>
                  {((zone.completed / zone.total) * 100).toFixed(1)}% Complete
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDailyTrends = () => {
    if (!reportData?.dailyData) {
      return (
        <div style={styles.noData}>
          <div style={styles.noDataIcon}>📈</div>
          <h3 style={styles.noDataTitle}>No daily trend data available</h3>
        </div>
      );
    }

    return (
      <div style={styles.fadeIn}>
        {/* Summary Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statContent}>
              <span style={styles.statLabel}>Total Jobs</span>
              <span style={styles.statValue}>{formatNumber(reportData.summary.total)}</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>✅</div>
            <div style={styles.statContent}>
              <span style={styles.statLabel}>Completion Rate</span>
              <span style={styles.statValue}>{reportData.summary.completion_rate}%</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>❌</div>
            <div style={styles.statContent}>
              <span style={styles.statLabel}>Cancellation Rate</span>
              <span style={styles.statValue}>{reportData.summary.cancellation_rate}%</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📅</div>
            <div style={styles.statContent}>
              <span style={styles.statLabel}>Avg Daily</span>
              <span style={styles.statValue}>{reportData.summary.avg_daily}</span>
            </div>
          </div>
        </div>

        {/* Daily Trends Chart */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Daily Job Trends</h3>
          <div style={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={reportData.dailyData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--system-separator)" />
                <XAxis 
                  dataKey="date" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80}
                  tick={{ fill: 'var(--system-secondary-label)', fontSize: 12 }}
                />
                <YAxis tick={{ fill: 'var(--system-secondary-label)' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--glass-background)', 
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: '12px'
                  }} 
                />
                <Legend />
                <Line type="monotone" dataKey="allocated" stroke="#007AFF" strokeWidth={2} name="Allocated" />
                <Line type="monotone" dataKey="completed" stroke="#34C759" strokeWidth={2} name="Completed" />
                <Line type="monotone" dataKey="cancelled" stroke="#FF3B30" strokeWidth={2} name="Cancelled" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderAgeingBuckets = () => {
    if (!reportData?.ageingBuckets) {
      return (
        <div style={styles.noData}>
          <div style={styles.noDataIcon}>⏰</div>
          <h3 style={styles.noDataTitle}>No ageing data available</h3>
        </div>
      );
    }
  
    return (
      <div style={styles.fadeIn}>
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="stat-card">
            <div style={{ fontSize: '32px', textAlign: 'center' }}>📦</div>
            <h3 style={{ textAlign: 'center' }}>0-3 Days</h3>
            <p style={{ fontSize: '36px', fontWeight: 'bold', textAlign: 'center', color: '#34C759' }}>
              {reportData.ageingBuckets['0-3 days']}
            </p>
          </div>
          <div className="stat-card">
            <div style={{ fontSize: '32px', textAlign: 'center' }}>⚠️</div>
            <h3 style={{ textAlign: 'center' }}>4-7 Days</h3>
            <p style={{ fontSize: '36px', fontWeight: 'bold', textAlign: 'center', color: '#FF9500' }}>
              {reportData.ageingBuckets['4-7 days']}
            </p>
          </div>
          <div className="stat-card">
            <div style={{ fontSize: '32px', textAlign: 'center' }}>🔥</div>
            <h3 style={{ textAlign: 'center' }}>7 Days</h3>
            <p style={{ fontSize: '36px', fontWeight: 'bold', textAlign: 'center', color: '#FF3B30' }}>
              {reportData.ageingBuckets['>7 days']}
            </p>
          </div>
        </div>
        <div className="stat-card" style={{ marginTop: '20px', textAlign: 'center' }}>
          <h3>Total Open Calls</h3>
          <p style={{ fontSize: '32px', fontWeight: 'bold' }}>{reportData.totalOpen}</p>
        </div>
      </div>
    );
  };
  
  const renderBrandSLA = () => {
    if (!reportData?.brandPerformance) {
      return (
        <div style={styles.noData}>
          <div style={styles.noDataIcon}>🏷️</div>
          <h3 style={styles.noDataTitle}>No brand SLA data available</h3>
        </div>
      );
    }
  
    return (
      <div style={styles.fadeIn}>
        <div className="premium-table-container">
          <h3>Brand SLA Performance</h3>
          <table className="premium-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>SLA (Days)</th>
                <th>Total Jobs</th>
                <th>Avg TAT</th>
                <th>SLA Compliance</th>
              </tr>
            </thead>
            <tbody>
              {reportData.brandPerformance.map(brand => (
                <tr key={brand.brand}>
                  <td><strong>{brand.brand}</strong></td>
                  <td>{brand.sla_days}</td>
                  <td>{brand.total_jobs}</td>
                  <td>{brand.avg_tat} days</td>
                  <td>
                    <span className={`premium-badge ${brand.sla_compliance > 80 ? 'success' : 'warning'}`}>
                      {brand.sla_compliance}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Performance Analytics</h1>

      {/* Message Display */}
      {message.text && (
        <div className={`message-toast ${message.type}`} style={{ position: 'static', marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {/* Controls */}
      <div style={styles.controlsCard}>
        <div style={styles.controlRow}>
          <div style={styles.controlGroup}>
            <label style={styles.controlLabel}>Report Type</label>
            <select 
              value={reportType} 
              onChange={(e) => setReportType(e.target.value)}
              className="premium-select"
              style={styles.controlSelect}
            >
              <option value="asc-performance">ASC Performance</option>
              <option value="tat-report">TAT & SLA Report</option>
              <option value="brand-performance">Brand Performance</option>
              <option value="zone-performance">Zone Performance</option>
              <option value="daily-trends">Daily Trends</option>
              <option value="ageing-buckets">Ageing Buckets</option>
              <option value="brand-sla">Brand SLA Report</option>
            </select>
          </div>

          <div style={styles.controlGroup}>
            <label style={styles.controlLabel}>Date Range</label>
            <div style={styles.dateRange}>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                className="premium-input"
                style={styles.dateInput}
              />
              <span style={styles.dateSeparator}>→</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                className="premium-input"
                style={styles.dateInput}
              />
            </div>
          </div>

          <div style={styles.controlGroup}>
            <label style={styles.controlLabel}>Download</label>
            <div style={styles.buttonGroup}>
              <button onClick={() => downloadReport('excel')} className="premium-button success" style={styles.downloadButton}>
                Excel
              </button>
              <button onClick={() => downloadReport('csv')} className="premium-button info" style={styles.downloadButton}>
                CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div className="loading-spinner" style={{ width: 40, height: 40 }} />
          <p style={styles.loadingText}>Loading report data...</p>
        </div>
      ) : (
        <div style={styles.reportContent}>
          {reportType === 'asc-performance' && renderASCPerformance()}
          {reportType === 'tat-report' && renderTATReport()}
          {reportType === 'brand-performance' && renderBrandPerformance()}
          {reportType === 'zone-performance' && renderZonePerformance()}
          {reportType === 'daily-trends' && renderDailyTrends()}
          {reportType === 'ageing-buckets' && renderAgeingBuckets()}
          {reportType === 'brand-sla' && renderBrandSLA()}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px',
    animation: 'fadeIn 0.3s ease'
  },
  header: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--system-label)',
    marginBottom: '32px',
    textAlign: 'center',
    letterSpacing: '-0.022em'
  },
  controlsCard: {
    background: 'var(--glass-background)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid var(--glass-border)',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '30px',
    boxShadow: 'var(--system-shadow-md)'
  },
  controlRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 2fr 1fr',
    gap: '24px',
    alignItems: 'flex-end'
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  controlLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--system-secondary-label)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  controlSelect: {
    height: '48px',
    fontSize: '15px'
  },
  dateRange: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  dateInput: {
    flex: 1,
    height: '48px'
  },
  dateSeparator: {
    color: 'var(--system-secondary-label)',
    fontSize: '18px'
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px'
  },
  downloadButton: {
    flex: 1,
    height: '48px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    background: 'var(--glass-background)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid var(--glass-border)',
    borderRadius: '20px',
    gap: '16px'
  },
  loadingText: {
    color: 'var(--system-secondary-label)',
    fontSize: '16px'
  },
  reportContent: {
    background: 'var(--glass-background)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid var(--glass-border)',
    borderRadius: '20px',
    padding: '30px',
    boxShadow: 'var(--system-shadow-lg)',
    animation: 'slideUp 0.4s ease'
  },
  section: {
    marginBottom: '40px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: 'var(--system-label)'
  },
  dateRangeBadge: {
    padding: '6px 12px',
    background: 'var(--system-fill)',
    borderRadius: '20px',
    fontSize: '13px',
    color: 'var(--system-secondary-label)'
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '16px',
    background: 'var(--system-background)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  tableRow: {
    transition: 'all 0.2s ease',
    ':hover': {
      background: 'var(--system-fill)',
      transform: 'scale(1.01)'
    }
  },
  rankBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },
  tatCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  tatIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '4px'
  },
  emailSuccessCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  miniProgress: {
    width: '60px',
    height: '4px',
    background: 'var(--system-fill)',
    borderRadius: '2px',
    overflow: 'hidden'
  },
  miniProgressFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.3s ease'
  },
  progressBar: {
    width: '100%',
    height: '8px',
    background: 'var(--system-fill)',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    background: 'var(--system-background)',
    padding: '20px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: 'var(--system-shadow-sm)',
    transition: 'all 0.2s ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--system-shadow-md)'
    }
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '16px',
    background: 'var(--system-fill)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px'
  },
  statContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  statLabel: {
    fontSize: '13px',
    color: 'var(--system-secondary-label)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--system-blue)'
  },
  chartCard: {
    background: 'var(--system-background)',
    padding: '24px',
    borderRadius: '16px',
    marginBottom: '30px',
    boxShadow: 'var(--system-shadow-sm)'
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--system-label)',
    marginBottom: '20px'
  },
  chartWrapper: {
    width: '100%',
    height: '400px'
  },
  chartRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '30px'
  },
  zoneGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px'
  },
  zoneCard: {
    background: 'var(--system-background)',
    padding: '20px',
    borderRadius: '16px',
    boxShadow: 'var(--system-shadow-sm)',
    transition: 'all 0.2s ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--system-shadow-md)'
    }
  },
  zoneHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  zoneBadge: {
    padding: '4px 8px',
    background: 'var(--system-fill)',
    borderRadius: '12px',
    fontSize: '12px',
    color: 'var(--system-secondary-label)'
  },
  zoneStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '16px'
  },
  zoneStat: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  zoneStatLabel: {
    fontSize: '13px',
    color: 'var(--system-secondary-label)'
  },
  zoneStatValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--system-label)'
  },
  zoneProgress: {
    marginTop: '12px'
  },
  zoneProgressLabel: {
    display: 'block',
    textAlign: 'right',
    fontSize: '12px',
    color: 'var(--system-secondary-label)',
    marginTop: '4px'
  },
  tatBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    color: 'white'
  },
  successBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    color: 'white'
  },
  noData: {
    textAlign: 'center',
    padding: '60px',
    background: 'var(--system-fill)',
    borderRadius: '16px'
  },
  noDataIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5
  },
  noDataTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--system-label)',
    marginBottom: '8px'
  },
  noDataText: {
    fontSize: '14px',
    color: 'var(--system-secondary-label)'
  },
  fadeIn: {
    animation: 'fadeIn 0.4s ease'
  }
};

// Add keyframes
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(style);

export default PerformanceReports;